/**
 * Package L1（category_key）/ L2 SPA picker
 * selectVariant：写 master id → 同步 option → 价格/sticky ATC → 手风琴折叠 → replaceState
 * 进度条分母：变体 custom.inventory_total（data-inventory-total）
 * 卡片 data-display-price / data-display-compare：与卡片展示价一致，用于同步主价/ATC
 */
(function () {
  if (customElements.get('oc-mr-package-picker')) return;

  class OcMrPackagePicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.productId = this.dataset.productId;
      this.formId = this.dataset.formId;
      this.moneyFormat =
        this.dataset.moneyFormat ||
        (window.theme && theme.settings && theme.settings.moneyFormat) ||
        (window.theme && theme.moneyFormat) ||
        window.moneyFormat ||
        (window.Shopify && Shopify.money_format) ||
        '£{{amount}}';
      this.saveLabel =
        this.dataset.saveLabel ||
        (window.theme && theme.strings && theme.strings.savePrice
          ? null
          : 'Save');
      this.urgencyTemplate = '';
      try {
        const tpl = this.querySelector('[data-package-urgency-template]');
        if (tpl) this.urgencyTemplate = JSON.parse(tpl.textContent || '""') || '';
      } catch (e) {
        this.urgencyTemplate = '';
      }

      this.variants = [];
      try {
        const jsonEl = this.querySelector('[data-package-variants-json]');
        if (jsonEl) this.variants = JSON.parse(jsonEl.textContent || '[]') || [];
      } catch (e) {
        this.variants = [];
      }

      this.cardsRoot = this.querySelector('[data-package-cards]');
      this.l1 = this.querySelector('[data-package-l1]');

      this.addEventListener('click', this.onClick.bind(this));

      this._syncMobileTagLayout = this.syncMobileTagLayout.bind(this);
      this._onTagResize = () => {
        clearTimeout(this._tagResizeTimer);
        this._tagResizeTimer = window.setTimeout(this._syncMobileTagLayout, 150);
      };
      window.addEventListener('resize', this._onTagResize);
      window.requestAnimationFrame(this._syncMobileTagLayout);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this._syncMobileTagLayout();
        });
      }

      const params = new URLSearchParams(window.location.search);
      const urlVariant = params.get('variant');
      if (urlVariant) {
        this.selectVariant(urlVariant, { updateHistory: false, fromInit: true });
      } else {
        this.syncStockUI();
        this.collapseUnselectedBodies();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onTagResize);
      clearTimeout(this._tagResizeTimer);
      this._bound = false;
    }

    onClick(e) {
      const catBtn = e.target.closest('[data-package-l1] button[data-category-key]');
      if (catBtn && this.contains(catBtn)) {
        e.preventDefault();
        this.onCategoryClick(catBtn);
        return;
      }

      const selectBtn = e.target.closest('[data-package-select]');
      if (selectBtn && this.contains(selectBtn)) {
        e.preventDefault();
        const card = selectBtn.closest('[data-package-card]');
        if (card) this.selectVariant(card.dataset.variantId, { updateHistory: true });
      }
    }

    onCategoryClick(btn) {
      const key = btn.dataset.categoryKey || '';
      this.querySelectorAll('[data-package-l1] [data-category-key]').forEach((el) => {
        const active = el === btn;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const cards = this.querySelectorAll('[data-package-card]');
      let firstVisible = null;
      cards.forEach((card) => {
        const cardKey = card.dataset.categoryKey || '';
        const match = !key || cardKey === key;
        card.classList.toggle('is-hidden', !match);
        if (match) {
          card.hidden = false;
          if (!firstVisible && card.dataset.available !== 'false') {
            firstVisible = card;
          } else if (!firstVisible) {
            firstVisible = card;
          }
        } else {
          card.hidden = true;
          card.classList.remove('is-selected', 'is-expanded');
          const body = card.querySelector('[data-package-body]');
          if (body) body.hidden = true;
        }
      });

      if (firstVisible) {
        this.selectVariant(firstVisible.dataset.variantId, { updateHistory: true });
      }
      window.requestAnimationFrame(() => this.syncMobileTagLayout());
    }

    getVariantById(id) {
      const sid = String(id);
      return this.variants.find((v) => String(v.id) === sid) || null;
    }

    getProductContainer() {
      return (
        this.closest('.europe-product-container') ||
        this.closest('[data-section-type="product"]') ||
        this.closest('.product-grid__container') ||
        this.closest('.product-single') ||
        this.closest('[id^="shopify-section-"]') ||
        document.querySelector('.europe-product-container') ||
        document.querySelector('[data-section-type="product"]') ||
        document.querySelector('.product-grid__container')
      );
    }

    getForm() {
      if (this.formId) {
        const byId = document.getElementById(this.formId);
        if (byId) return byId;
      }
      return (
        this.getProductContainer()?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    /** 用卡片展示价同步主价/ATC（与卡片渲染一致） */
    resolveDisplayVariant(variant, card) {
      if (!variant) return null;
      const next = Object.assign({}, variant);
      if (card) {
        const dp = parseInt(card.dataset.displayPrice, 10);
        const dc = parseInt(card.dataset.displayCompare, 10);
        if (!isNaN(dp) && dp > 0) next.price = dp;
        if (!isNaN(dc) && dc > 0) next.compare_at_price = dc;
        else if (!isNaN(dc) && dc === 0) next.compare_at_price = null;
      }
      return next;
    }

    /** 仅展开当前选中卡；无库存且无 Includes 时不展开灰底 */
    collapseUnselectedBodies(selectedId) {
      const sid = selectedId != null ? String(selectedId) : String(this.dataset.selectedVariant || '');
      this.querySelectorAll('[data-package-card]').forEach((card) => {
        const selected = sid !== '' && String(card.dataset.variantId) === sid;
        const hasBody = this.cardHasBodyContent(card);
        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-expanded', selected && hasBody);
        const body = card.querySelector('[data-package-body]');
        if (body) body.hidden = !(selected && hasBody);
      });
    }

    cardHasBodyContent(card) {
      if (!card) return false;
      const stockEl = card.querySelector('[data-package-stock]');
      const includesEl = card.querySelector('[data-package-includes]');
      const stockOn = Boolean(stockEl && !stockEl.hidden);
      const includesOn = Boolean(includesEl);
      const has = stockOn || includesOn;
      card.dataset.hasBodyContent = has ? 'true' : 'false';
      return has;
    }

    refreshBodyVisibility(card) {
      if (!card) return;
      const hasBody = this.cardHasBodyContent(card);
      const selected = card.classList.contains('is-selected');
      const body = card.querySelector('[data-package-body]');
      card.classList.toggle('is-expanded', selected && hasBody);
      if (body) body.hidden = !(selected && hasBody);
    }

    /** 同步 option radio/select，保证主题 Variants 状态与 id 一致 */
    syncOptionInputs(variant) {
      const container = this.getProductContainer();
      if (!container || !variant) return;

      ['option1', 'option2', 'option3'].forEach((key, i) => {
        const val = variant[key];
        if (val == null || val === '') return;
        const index = 'option' + (i + 1);
        const inputs = container.querySelectorAll(
          '[data-variant-input][data-index="' + index + '"]'
        );
        inputs.forEach((input) => {
          const type = input.getAttribute('type');
          if (type === 'radio' || type === 'checkbox') {
            input.checked = input.value === val;
          } else if (input.tagName === 'SELECT') {
            input.value = val;
          }
        });
      });
    }

    selectVariant(id, opts) {
      const options = opts || {};
      const baseVariant = this.getVariantById(id);
      if (!baseVariant) return;

      const card =
        this.querySelector('[data-package-card][data-variant-id="' + String(id) + '"]') ||
        null;
      const variant = this.resolveDisplayVariant(baseVariant, card);

      const form = this.getForm();
      const container = this.getProductContainer();

      if (form) {
        const master = form.querySelector('[data-product-select], select[name="id"]');
        const inputs = form.querySelectorAll('[name="id"]');
        inputs.forEach((input) => {
          input.value = baseVariant.id;
        });
        if (master) {
          master.value = baseVariant.id;
          // 不派发 master change：避免其它监听误读；价格由本组件直接写 DOM
        }
      }

      this.syncOptionInputs(baseVariant);
      this.collapseUnselectedBodies(baseVariant.id);

      if (card && card.dataset.categoryKey && this.l1) {
        const catKey = card.dataset.categoryKey;
        this.querySelectorAll('[data-package-l1] [data-category-key]').forEach((el) => {
          const active = el.dataset.categoryKey === catKey;
          el.classList.toggle('is-active', active);
          el.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }

      this.updatePriceDom(container, variant);

      const detail = { variant: variant };
      if (container) {
        container.dispatchEvent(new CustomEvent('variantChange', { detail, bubbles: true }));
        container.dispatchEvent(new CustomEvent('variantPriceChange', { detail, bubbles: true }));
        if (baseVariant.featured_image) {
          container.dispatchEvent(
            new CustomEvent('variantImageChange', {
              detail: { variant: baseVariant },
              bubbles: true,
            })
          );
        }
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail }));

      this.refreshStickyPrice();
      this.syncAddToCartButton(baseVariant);
      this.syncStockUI(baseVariant);
      if (typeof window.syncStickyAtcExtras === 'function') {
        window.syncStickyAtcExtras(baseVariant);
      }

      if (options.updateHistory !== false && history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', baseVariant.id);
        history.replaceState({ path: url.href }, '', url.href);
      }

      this.dataset.selectedVariant = String(baseVariant.id);
    }

    formatMoney(cents) {
      var format = this.moneyFormat || '£{{amount}}';
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, format || theme.settings.moneyFormat);
      }
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
      return this.formatMoneyFallback(cents, format);
    }

    /** Shopify money 占位符简易实现，保证货币符号不丢 */
    formatMoneyFallback(cents, format) {
      var value = Number(cents) || 0;
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format || '£{{amount}}';

      function withDelimiters(number, precision, thousands, decimal) {
        precision = precision == null ? 2 : precision;
        thousands = thousands || ',';
        decimal = decimal || '.';
        var fixed = (number / 100.0).toFixed(precision);
        var parts = fixed.split('.');
        var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return parts[1] != null ? dollars + decimal + parts[1] : dollars;
      }

      var token = (formatString.match(placeholderRegex) || [])[1] || 'amount';
      var amount = withDelimiters(value, 2);
      switch (token) {
        case 'amount_no_decimals':
          amount = withDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          amount = withDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = withDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          amount = withDelimiters(value, 2, "'", '.');
          break;
        default:
          amount = withDelimiters(value, 2);
      }
      return formatString.replace(placeholderRegex, amount);
    }

    /** 节省金额银行家舍入到整数货币单位（分）。无法展示时返回 null */
    roundHalfEvenSavings(compareAt, price) {
      var original = parseInt(compareAt, 10) || 0;
      var sale = parseInt(price, 10) || 0;
      if (!(original > 0) || !(sale > 0) || sale >= original) return null;
      var diff = original - sale;
      var whole = Math.floor(diff / 100);
      var frac = diff % 100;
      if (frac > 50) whole += 1;
      else if (frac === 50 && whole % 2 === 1) whole += 1;
      if (whole <= 0) return null;
      return whole * 100;
    }

    /** 去掉货币字符串末尾的 ,00 / .00，保留千分位 */
    stripMoneyDecimals(text) {
      return String(text).replace(/[.,]00(?!\d)/g, '');
    }

    formatSavings(compareAt, price) {
      var rounded = this.roundHalfEvenSavings(compareAt, price);
      if (rounded == null) return '';
      return this.stripMoneyDecimals(this.formatMoney(rounded));
    }

    /** 对齐 oc-revamp-price：`Save` + 金额（或 theme.strings.savePrice） */
    formatSaveText(amount) {
      if (typeof theme !== 'undefined' && theme.strings && theme.strings.savePrice) {
        return theme.strings.savePrice.replace('[saved_amount]', amount);
      }
      var label = (this.saveLabel || 'Save').trim();
      if (!label) label = 'Save';
      if (label.indexOf('[saved_amount]') !== -1) {
        return label.replace('[saved_amount]', amount);
      }
      return label + ' ' + amount;
    }

    /** EU oc-revamp-price：.current-price / .single-price / .origin-price / [data-save-price] */
    updatePriceDom(container, variant) {
      var root = container || document;
      // 主价 + 底部 ATC（含未吸底时 display:none 的 .product-content），一并更新
      var priceBlocks = Array.prototype.slice.call(
        root.querySelectorAll(
          '.product__info-container > .product-price, .add-submit-container .product-price, .buy-button .product-price'
        )
      );
      if (!priceBlocks.length) {
        var fallback =
          root.querySelector('.product-infos .product-price') ||
          root.querySelector('.product-price') ||
          root.querySelector('.product-block--price');
        if (fallback) priceBlocks = [fallback];
      }

      var self = this;
      priceBlocks.forEach(function (priceBlock) {
        self.updateSinglePriceBlock(priceBlock, variant);
      });
    }

    updateSinglePriceBlock(priceBlock, variant) {
      if (!priceBlock || !variant) return;

      var priceEl =
        priceBlock.querySelector('.price-item--sale.current-price') ||
        priceBlock.querySelector('.current-price') ||
        priceBlock.querySelector('[data-product-price]') ||
        priceBlock.querySelector('.price-item--regular.single-price') ||
        priceBlock.querySelector('.single-price');
      var regularEl = priceBlock.querySelector('.price__regular .single-price');
      var compareEl =
        priceBlock.querySelector('.origin-price') ||
        priceBlock.querySelector('[data-compare-price]');
      var saveEl =
        priceBlock.querySelector('[data-save-price]') ||
        priceBlock.querySelector('.save-price');
      var priceContainer = priceBlock.querySelector('.oc-price-container') || priceBlock.querySelector('.price');

      var price = parseInt(variant.price, 10) || 0;
      var compareAt = parseInt(variant.compare_at_price, 10) || 0;
      var onSale = compareAt > price;
      var money = this.formatMoney(price);

      if (priceEl) {
        priceEl.innerHTML = money;
        if (priceEl.hasAttribute('data-value')) {
          priceEl.setAttribute('data-value', String(price));
        }
        priceEl.classList.toggle('on-sale', onSale);
      }
      if (regularEl && regularEl !== priceEl) {
        regularEl.innerHTML = money;
      }

      if (compareEl) {
        compareEl.innerHTML = onSale ? this.formatMoney(compareAt) : '';
        var compareWrap = compareEl.closest('span');
        if (compareWrap && compareWrap !== compareEl) {
          compareWrap.style.display = onSale ? '' : 'none';
        }
      }

      var amount = onSale ? this.formatSavings(compareAt, price) : '';
      var saveHtml = amount ? this.formatSaveText(amount) : '';
      if (saveEl) {
        if (saveHtml) {
          saveEl.classList.remove('hide');
          saveEl.innerHTML = saveHtml;
        } else {
          saveEl.classList.add('hide');
          saveEl.innerHTML = '';
        }
      }
      document.querySelectorAll('.europe-product-container .product-images .save-slide').forEach(function (slide) {
        if (slide === saveEl) return;
        if (saveHtml) {
          slide.classList.remove('hide');
          slide.innerHTML = saveHtml;
        } else {
          slide.classList.add('hide');
          slide.innerHTML = '';
        }
      });

      if (priceContainer) {
        priceContainer.classList.toggle('price--on-sale', onSale);
      }
    }

    refreshStickyPrice() {
      // 不依赖 .sticky：未吸底时 .product-content 仍在 DOM 中，需提前写好价格
      var mainPrice =
        document.querySelector('.product__info-container > .product-price') ||
        document.querySelector('.europe-product-container .product-infos .product-price') ||
        document.querySelector('.europe-product-container .product-price');
      if (!mainPrice) return;

      var html = mainPrice.innerHTML;
      document
        .querySelectorAll('.add-submit-container .product-price, .buy-button .product-price')
        .forEach(function (stickyPrice) {
          if (stickyPrice === mainPrice) return;
          stickyPrice.innerHTML = html;
        });
    }

    /**
     * 对齐历史 variant-radios → global.js toggleAddButton：
     * available=false 时 ATC 禁用并显示 Sold out（含 sticky / fixed 克隆）
     */
    syncAddToCartButton(variant) {
      if (!variant) return;

      var available = variant.available !== false;
      var strings = window.variantStrings || {};
      var soldOutText = strings.soldOut || 'Sold out';
      var addText = strings.addToCart || 'Add to cart';
      var buttons = [];

      function collect(btn) {
        if (btn && buttons.indexOf(btn) === -1) buttons.push(btn);
      }

      var form = this.getForm();
      if (form) {
        form.querySelectorAll('button[name="add"]').forEach(collect);
      }

      document
        .querySelectorAll(
          '.add-submit-container button[name="add"], .product-infos_fixed button[name="add"], .buy-button button[name="add"]'
        )
        .forEach(collect);

      buttons.forEach(function (btn) {
        var label = btn.querySelector(':scope > span') || btn.querySelector('span');
        if (available) {
          btn.removeAttribute('disabled');
          btn.classList.remove('product-form__submit_disabled');
          if (label) label.textContent = addText;
        } else {
          btn.setAttribute('disabled', 'disabled');
          btn.classList.add('product-form__submit_disabled');
          if (label) label.textContent = soldOutText;
        }
      });
    }

    syncStockUI(variant) {
      const inventories =
        window.inventories && this.productId ? window.inventories[this.productId] : null;

      this.querySelectorAll('[data-package-card]').forEach((card) => {
        const stockEl = card.querySelector('[data-package-stock]');
        if (stockEl) {
          let qty = parseInt(card.dataset.inventoryQty, 10);
          let total = parseInt(card.dataset.inventoryTotal, 10);
          const tracked = card.dataset.inventoryTracked === 'true';
          const vid = card.dataset.variantId;

          if (inventories && inventories[vid]) {
            qty = parseInt(inventories[vid].quantity, 10);
            card.dataset.inventoryQty = String(qty);
          }

          if (
            variant &&
            String(variant.id) === String(vid) &&
            typeof variant.inventory_quantity === 'number'
          ) {
            qty = variant.inventory_quantity;
          }

          if (isNaN(total)) total = 0;

          const show = tracked && qty > 0;
          stockEl.hidden = !show;

          if (show) {
            const barEl = stockEl.querySelector('[data-stock-bar]');
            const fill = stockEl.querySelector('[data-stock-fill]');
            const textEl = stockEl.querySelector('[data-stock-text]');

            if (barEl) {
              const showBar = total > 0;
              barEl.hidden = !showBar;
              if (showBar && fill) {
                const pct = Math.max(0, Math.min(100, (qty / total) * 100));
                fill.style.width = pct + '%';
              }
            }

            if (textEl && this.urgencyTemplate) {
              const parts = String(this.urgencyTemplate).split('[count]');
              textEl.innerHTML =
                this.escapeHtml(parts[0] || '') +
                '<span data-stock-count>' +
                qty +
                '</span>' +
                this.escapeHtml(parts[1] || '');
            } else if (textEl) {
              const countEl = textEl.querySelector('[data-stock-count]');
              if (countEl) countEl.textContent = String(qty);
            }
          }
        }

        this.refreshBodyVisibility(card);
      });
    }

    /** 移动端：一行放得下保持并排，放不下才把标签升到标题和价格上方 */
    sideBySideMetaWidth(card) {
      const header = card.querySelector('.oc-mr-package-card__header');
      const media = card.querySelector('.oc-mr-package-card__media');
      const pricing = card.querySelector('.oc-mr-package-card__pricing');
      if (!header || !media || !pricing) return 0;
      const hs = getComputedStyle(header);
      const gap = parseFloat(hs.columnGap) || parseFloat(hs.gap) || 0;
      const padX =
        parseFloat(hs.paddingLeft) +
        parseFloat(hs.paddingRight) +
        parseFloat(hs.borderLeftWidth) +
        parseFloat(hs.borderRightWidth);
      return (
        header.getBoundingClientRect().width -
        padX -
        media.getBoundingClientRect().width -
        pricing.getBoundingClientRect().width -
        gap * 2
      );
    }

    oneLineTagWidth(tag) {
      const probe = tag.cloneNode(true);
      probe.style.position = 'absolute';
      probe.style.left = '-9999px';
      probe.style.top = '0';
      probe.style.visibility = 'hidden';
      probe.style.whiteSpace = 'nowrap';
      probe.style.width = 'max-content';
      probe.style.maxWidth = 'none';
      probe.style.display = 'inline-flex';
      document.body.appendChild(probe);
      const width = probe.getBoundingClientRect().width;
      probe.remove();
      return width;
    }

    syncMobileTagLayout() {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      this.querySelectorAll('[data-package-card]').forEach((card) => {
        const tag = card.querySelector('.oc-mr-package-card__tag');
        if (!mobile || !tag) {
          card.classList.remove('is-tag-stacked');
          return;
        }
        if (card.hidden || card.classList.contains('is-hidden')) return;
        const available = this.sideBySideMetaWidth(card);
        if (available < 1) return;
        card.classList.toggle('is-tag-stacked', this.oneLineTagWidth(tag) > available + 1);
      });
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('oc-mr-package-picker', OcMrPackagePicker);
})();
